export type GrantOpportunityDocumentRole =
  | "attachment"
  | "rfp"
  | "application_draft"
  | "evidence"
  | "budget_draft"
  | "progress_report_draft"
  | "use_of_funds_draft"
  | "award_letter"
  | "package_source";

export const GRANT_OPPORTUNITY_DOCUMENT_ROLE_LABELS: Record<GrantOpportunityDocumentRole, string> = {
  attachment: "Attachment",
  rfp: "RFP",
  application_draft: "Application draft",
  evidence: "Evidence",
  budget_draft: "Budget draft",
  progress_report_draft: "Progress report draft",
  use_of_funds_draft: "Use-of-funds plan draft",
  award_letter: "Award letter",
  package_source: "Package file",
};

// The lifecycle of an AI Partner package file (ENG-1827). Non-package links
// are always "ready" — a finished file with nothing left to process.
export type GrantOpportunityDocumentProcessingStatus =
  | "queued"
  | "uploading"
  | "parsing"
  | "ready"
  | "failed"
  | "retrying";

export type GrantDraftExportFormat = "docx" | "pdf" | "csv";

export type GrantOpportunityDocumentLink = {
  id: number;
  grant_opportunity_id: number;
  document_id: number;
  name: string;
  file_path: string | null;
  label: string | null;
  role: GrantOpportunityDocumentRole;
  processing_status: GrantOpportunityDocumentProcessingStatus;
  error_message: string | null;
  linked_by_user_id: number;
  linked_by_name: string;
  created_at: string;
};

// Client-side mirror of GrantSuite::UploadOpportunityDocument's package
// rules, so an invalid file is rejected before any bytes leave the browser.
// Messages match config/locales/en.yml grant_suite.package.errors.
export const PACKAGE_FILE_EXTENSIONS = ["pdf", "docx", "xlsx", "csv", "txt", "md"] as const;
export const PACKAGE_FILE_ACCEPT = PACKAGE_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(",");
export const PACKAGE_MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

const IMAGE_FILE_EXTENSIONS = new Set([
  "bmp", "gif", "heic", "icns", "ico", "jpeg", "jpg", "png", "psd", "svg", "tif", "tiff", "webp",
]);

export function packageFileRejection(file: File): string | null {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (IMAGE_FILE_EXTENSIONS.has(extension) || file.type.startsWith("image/")) {
    return "Images can't be part of an opportunity package";
  }
  if (!(PACKAGE_FILE_EXTENSIONS as readonly string[]).includes(extension)) {
    return "Package files must be PDF, DOCX, XLSX, CSV, TXT, or Markdown";
  }
  if (file.size > PACKAGE_MAX_FILE_SIZE_BYTES) {
    return "Package files must be 50 MB or smaller";
  }
  return null;
}

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

function documentsPath(municipalityId: number, opportunityId: number): string {
  return `/api/v1/municipalities/${municipalityId}/grant_opportunities/${opportunityId}/documents`;
}

export async function listGrantOpportunityDocuments(
  municipalityId: number,
  opportunityId: number,
): Promise<GrantOpportunityDocumentLink[]> {
  const response = await fetch(documentsPath(municipalityId, opportunityId), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error("Failed to load linked documents");
  }
  return response.json();
}

export async function linkGrantOpportunityDocument(
  municipalityId: number,
  opportunityId: number,
  documentId: number,
  options?: { label?: string; role?: GrantOpportunityDocumentRole },
): Promise<GrantOpportunityDocumentLink> {
  const response = await fetch(documentsPath(municipalityId, opportunityId), {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({
      grant_opportunity_document: {
        document_id: documentId,
        label: options?.label ?? null,
        role: options?.role ?? "attachment",
      },
    }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.errors?.join(", ") ?? "Failed to link document");
  }
  return response.json();
}

export async function uploadGrantOpportunityDocument(
  municipalityId: number,
  opportunityId: number,
  upload: { file: File; name?: string; role?: GrantOpportunityDocumentRole },
): Promise<GrantOpportunityDocumentLink> {
  const body = new FormData();
  body.append("grant_opportunity_document[file]", upload.file);
  if (upload.name) {
    body.append("grant_opportunity_document[name]", upload.name);
  }
  body.append("grant_opportunity_document[role]", upload.role ?? "attachment");

  const response = await fetch(`${documentsPath(municipalityId, opportunityId)}/upload`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
    body,
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.errors?.join(", ") ?? "Failed to upload file");
  }
  return response.json();
}

// Requeues a failed package file through the parse pipeline (ENG-1826).
// The server only accepts failed package files; anything else 422s.
export async function retryGrantOpportunityDocumentProcessing(
  municipalityId: number,
  opportunityId: number,
  linkId: number,
): Promise<GrantOpportunityDocumentLink> {
  const response = await fetch(
    `${documentsPath(municipalityId, opportunityId)}/${linkId}/retry_processing`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
    },
  );
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.errors?.join(", ") ?? "Failed to retry file");
  }
  return response.json();
}

export async function unlinkGrantOpportunityDocument(
  municipalityId: number,
  opportunityId: number,
  linkId: number,
): Promise<void> {
  const response = await fetch(`${documentsPath(municipalityId, opportunityId)}/${linkId}`, {
    method: "DELETE",
    credentials: "include",
    headers: { "X-CSRF-Token": csrfToken() },
  });
  if (!response.ok) {
    throw new Error("Failed to unlink document");
  }
}
